import { Controller } from "@hotwired/stimulus"

// Venue photo carousel (event landing pages).
//
// Progressive enhancement: without JS every slide is visible and the markup
// reads as a plain vertical list of photos, which is a perfectly good fallback.
// `is-enhanced` is what collapses it into a single-slide carousel, and it is
// only ever added from here — so a bundle that fails to load degrades to the
// list rather than to one photo with dead arrows.
//
// The carousel advances on a timer. WCAG 2.2.2 wants a way to stop moving
// content, so autoplay pauses on hover and on focus, halts while the tab is in
// the background, and stops for good the moment someone works the arrows or
// dots — once you're steering, we get out of the way.
export default class extends Controller {
  static targets = ["slide", "dot", "status"]
  static values = {
    index: { type: Number, default: 0 },
    interval: { type: Number, default: 6000 }
  }

  connect() {
    this.element.classList.add("is-enhanced")
    this.show(this.indexValue)

    this.visibilityListener = () => {
      document.hidden ? this.stopTimer() : this.startTimer()
    }
    document.addEventListener("visibilitychange", this.visibilityListener)

    this.startTimer()
  }

  disconnect() {
    this.stopTimer()
    document.removeEventListener("visibilitychange", this.visibilityListener)

    this.element.classList.remove("is-enhanced")
    this.slideTargets.forEach(slide => {
      slide.classList.remove("is-active")
      slide.removeAttribute("aria-hidden")
    })
  }

  // ── autoplay ──────────────────────────────────────────────────────────────

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Only meaningful while a timer is already running — Stimulus also fires this
  // during initialisation, before connect() has started anything.
  intervalValueChanged() {
    if (this.timer) this.startTimer()
  }

  startTimer() {
    this.stopTimer()
    if (this.autoplayStopped || this.paused || this.prefersReducedMotion) return
    if (this.slideTargets.length < 2) return

    this.timer = setInterval(() => this.show(this.indexValue + 1, 1), this.intervalValue)
  }

  stopTimer() {
    if (!this.timer) return

    clearInterval(this.timer)
    this.timer = null
  }

  // Hover and keyboard focus only suspend the timer; it resumes on the way out.
  pause() {
    this.paused = true
    this.stopTimer()
  }

  resume() {
    this.paused = false
    this.startTimer()
  }

  // ── navigation ────────────────────────────────────────────────────────────

  // next/previous/select are the deliberate controls, so each one retires the
  // timer for good. The timer itself advances through `show`.
  //
  // Direction is passed explicitly here because it cannot be inferred when the
  // index wraps: going "next" from the last photo lands on a lower index, and
  // the slide still has to travel forwards.
  next() {
    this.stopAutoplay()
    this.show(this.indexValue + 1, 1)
  }

  previous() {
    this.stopAutoplay()
    this.show(this.indexValue - 1, -1)
  }

  select(event) {
    this.stopAutoplay()
    this.show(this.dotTargets.indexOf(event.currentTarget))
  }

  // Arrow keys move between photos once the carousel itself has focus, which is
  // the behaviour a keyboard user expects from a composite widget.
  navigate(event) {
    if (event.key === "ArrowRight") this.next()
    else if (event.key === "ArrowLeft") this.previous()
    else return

    event.preventDefault()
  }

  stopAutoplay() {
    this.autoplayStopped = true
    this.stopTimer()
  }

  show(index, direction) {
    const count = this.slideTargets.length
    if (count === 0) return

    // Wrap in both directions so neither the arrows nor the timer dead-end.
    const target = ((index % count) + count) % count
    const from = this.currentIndex
    if (from === target) return

    // A dot jump has no inherent direction, so take it from the index it moves.
    const forward = direction !== undefined ? direction > 0 : target > from
    this.currentIndex = target
    this.indexValue = target

    this.slideTargets.forEach((slide, i) => {
      const active = i === target
      slide.classList.toggle("is-active", active)
      // Off-screen slides are still painted, so keep them out of the
      // accessibility tree.
      slide.setAttribute("aria-hidden", active ? "false" : "true")
    })

    from === undefined ? this.paint(target) : this.slide(target, from, forward)

    this.dotTargets.forEach((dot, i) => {
      const active = i === target
      dot.classList.toggle("is-active", active)
      dot.setAttribute("aria-selected", active ? "true" : "false")
      dot.setAttribute("tabindex", active ? "0" : "-1")
    })

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = `Photo ${target + 1} of ${count}`
    }
  }

  // ── slide transitions ─────────────────────────────────────────────────────

  // First paint: no animation, just put the active slide in place.
  paint(target) {
    this.slideTargets.forEach((slide, i) => {
      this.place(slide, i === target ? "0" : "100%", { animate: false })
    })
  }

  // The outgoing slide exits the way we are travelling and the incoming one
  // enters from the opposite edge; everything else is parked out of frame
  // silently, ready to come in from either side next time.
  slide(target, from, forward) {
    const exit = forward ? "-100%" : "100%"
    const enter = forward ? "100%" : "-100%"

    this.slideTargets.forEach((slide, i) => {
      if (i === target) return

      this.place(slide, i === from ? exit : enter, { animate: i === from })
    })

    const incoming = this.slideTargets[target]
    this.place(incoming, enter, { animate: false })
    this.place(incoming, "0", { animate: true })
  }

  place(slide, offset, { animate }) {
    slide.style.transition = animate ? "" : "none"
    slide.style.transform = `translateX(${offset})`

    // Flush the un-animated position so a transition set immediately afterwards
    // starts from here rather than being collapsed into a single style change.
    if (!animate) void slide.offsetWidth
  }
}
